/**
 * Fix S (2026-10-06) — THE ONE STATUS MODEL. Every surface that tells a
 * player what is happening in their tournament (the Details tab's status
 * card, the board's idle card, the rail's own tab-switch effect) reads this
 * SAME pure function instead of deciding separately — that disagreement, and
 * the gaps it left, is exactly what `FIX_S_TOURNAMENT_CLARITY_SPEC.md` §0
 * records the owner hitting.
 *
 * PURE: no hooks, no `Date.now()` inside — `nowMs` is an input, already
 * clock-skew-corrected by the caller (`serverClock.ts`'s `nowServerMs()`).
 * The branch order below is `FIX_S_TOURNAMENT_CLARITY_SPEC.md` §S1's table,
 * TRANSCRIBED — first match wins, and the copy is EXACT (fixed by the spec;
 * the owner has not reviewed it yet), so it must not be paraphrased when this
 * file is next touched.
 */
import type {TournamentRawDetails} from '../detailsShape';

export type TournamentPhase =
  | 'notJoined' // I am not in this tournament
  | 'registered' // I am in; it has not started
  | 'awaitingArbiter' // manual round control; a human starts it
  | 'pairing' // pairings are being generated
  | 'paired' // pairings published, my board not up yet
  | 'playing' // the round is live and I have a game
  | 'bye' // I have a bye this round
  | 'sittingOut' // the round is live and I am not in it
  | 'break' // the round is over, the next one is coming
  | 'ended'
  | 'withdrawn'
  // Fix T / T1 — arena-only phases (the Swiss rows above never produce these).
  | 'pooled' // between arena games, waiting to be paired again
  | 'paused' // paused, out of the pool, no game right now
  | 'ending'; // the arena's buzzer has gone, no new games will start

export type StatusTone = 'idle' | 'wait' | 'live' | 'good' | 'warn' | 'done';

export type TournamentStatus = {
  phase: TournamentPhase;
  /** the one line that must never be ambiguous */
  headline: string;
  /** the "what this means for me" line; null when the headline says it all */
  detail: string | null;
  /** epoch ms the countdown runs to, or null */
  countdownTo: number | null;
  /** the countdown's own prefix, e.g. 'Tournament starts in' */
  countdownLabel: string | null;
  tone: StatusTone;
  /** the round this status is about, or null before round 1 */
  round: number | null;
};

/**
 * Fix T / T1 — the arena family's own status fields, built by
 * `statusInputFrom` from `arena.status` / `arena.standings` with the
 * fallbacks named on each field below (the old `ArenaLobby`'s own fallback
 * chain, §1.1/§1.4 of the spec's "facts established").
 */
export type ArenaStatusInput = {
  /** status.state || standings.state || null (the ArenaLobby fallback) */
  state: string | null;
  /** status.myState */
  myState: string | null;
  /** !!status.paused || myState === 'paused' */
  paused: boolean;
  /** status.endsAtMs || standings.endsAtMs || 0 */
  endsAtMs: number;
  /** 0 = unranked */
  rank: number;
  points: number;
  poolSize: number;
};

export type TournamentStatusInput = {
  nowMs: number;
  /** null on the board card, which has no query (G-x3) */
  raw: TournamentRawDetails | null;
  joined: boolean; // joinedLocal ?? raw.is_joined
  withdrawn: boolean; // tournament.withdraw.status === 'ok'
  ended: boolean;
  roundState: string | null;
  statusMessage: string | null;
  currentRound: number | null;
  pairingGenerated: boolean;
  ongoingGames: number | null;
  roundStartAt: number | null;
  // `round` is nullable here (matching the core's `TournamentNextRound` —
  // `next_round_timer_response` can omit it): the caller passes the core
  // field straight through, so this must stay as wide as that field is.
  nextRound: {round: number | null; startAt: number} | null;
  bye: {round: number | null; at: number | null} | null;
  /** a live board of MINE is on the stage */
  myGameLive: boolean;
  /** my opponent's display name when myGameLive, for the detail line */
  opponentName: string | null;
  /** Fix T / T1 — which branch table this input takes. */
  family: 'swiss' | 'arena';
  /** null until an arena status/standings has ever landed (or !arena). */
  arena: ArenaStatusInput | null;
};

const TONE: Record<TournamentPhase, StatusTone> = {
  ended: 'done',
  withdrawn: 'done',
  playing: 'live',
  bye: 'good',
  pairing: 'wait',
  paired: 'wait',
  break: 'wait',
  sittingOut: 'warn',
  notJoined: 'idle',
  registered: 'idle',
  awaitingArbiter: 'idle',
  // Fix T / T1 — the arena-only phases.
  ending: 'wait',
  pooled: 'wait',
  paused: 'warn',
};

/**
 * The one builder both branch tables share (T1: "same build helper, same
 * tones"). `round` is the only thing that differs between them: the Swiss
 * table's own `R`/`round` locals, or `null` always for an arena row (T1:
 * "`round` is null for every arena row").
 */
const buildStatus = (
  phase: TournamentPhase,
  headline: string,
  detail: string | null,
  countdownTo: number | null,
  countdownLabel: string | null,
  round: number | null,
): TournamentStatus => ({
  phase,
  headline,
  detail,
  countdownTo,
  countdownLabel,
  tone: TONE[phase],
  round,
});

/**
 * "1 game live" / "2 games live" — fix AA (2026-10-06): the owner's
 * screenshot caught the old literal template reading "1 games live" for a
 * single ongoing game. Shared by row 5 (the bye card) and row 10.
 */
const liveGamesSuffix = (count: number | null): string =>
  count ? ` · ${count} game${count === 1 ? '' : 's'} live` : '';

/**
 * S1.1 — "late" is a derived predicate, HONEST about what the response does
 * NOT carry: there is no `joined_at` for ME on the details response (and this
 * file must not invent one — PARITY §S-x2). The only signal this lane has is
 * a joined player with no game and no bye while a round is live, so "late" is
 * narrowed to the one case that is unambiguous: `round1_paired_at` is set
 * (round 1 has already been paired) AND I have no bye on record AT ALL AND
 * the round in question IS round 1 — i.e. I cannot possibly have been paired
 * for it, because I joined after it happened. For round > 1 with no bye and
 * no board, `tournamentStatusFrom`'s sittingOut branch uses the honest
 * "you're not paired this round" instead of guessing why.
 */
export const isLateEntrant = ({
  raw,
  bye,
  round,
}: {
  raw: TournamentRawDetails | null;
  bye: {round: number | null; at: number | null} | null;
  round: number;
}): boolean => !!raw?.round1_paired_at && bye === null && round === 1;

/**
 * S10.1 — "has the round actually started". NOT `pairingGenerated` alone:
 * `reducers/tournament.ts`'s `tournamentGameEnd` sets that flag false on
 * EVERY `tournament_game_end_response` (~1/s during a round, any board in
 * the tournament), so it is false for everyone from the first finished game
 * onward. Gating on it alone sent a player whose own game had ended to row
 * 13 ("Starting soon…") in the middle of round 1.
 *
 * Exported (fix AA, 2026-10-06) so `rail/tournament/useByeProbe.ts` asks the
 * EXACT same question this file's own row 10 does, rather than a second
 * copy that could drift from it.
 */
export const roundStartedFrom = ({
  currentRound,
  pairingGenerated,
  roundState,
  ongoingGames,
}: {
  currentRound: number | null;
  pairingGenerated: boolean;
  roundState: string | null;
  ongoingGames: number | null;
}): boolean =>
  (currentRound ?? 0) > 0 ||
  pairingGenerated ||
  roundState === 'in_progress' ||
  (ongoingGames ?? 0) > 0;

export function tournamentStatusFrom(
  i: TournamentStatusInput,
): TournamentStatus {
  // Fix T / T1 — the arena branch of the ONE status model. The Swiss rows
  // below are untouched; `arenaStatusFrom` is its own first-match-wins table,
  // in this same file, sharing `buildStatus` and `TONE`.
  if (i.family === 'arena') {
    return arenaStatusFrom(i);
  }
  /** the round the COPY refers to — defaults to 1 before round 1 pairs */
  const R = i.currentRound && i.currentRound > 0 ? i.currentRound : 1;
  /** the STRUCTURED round field — null before round 1, unlike R above */
  const round = i.currentRound && i.currentRound > 0 ? i.currentRound : null;
  const total = i.raw?.rounds ?? 0;
  const isFinalRound = total > 0 && R >= total;
  const startDateMs = i.raw?.start_date ? Date.parse(i.raw.start_date) : NaN;
  const hasStartDate = Number.isFinite(startDateMs);
  const startInFuture = hasStartDate && startDateMs > i.nowMs;
  // S10.1: see `roundStartedFrom`'s own comment for why this is not
  // `pairingGenerated` alone.
  const roundStarted = roundStartedFrom({
    currentRound: i.currentRound,
    pairingGenerated: i.pairingGenerated,
    roundState: i.roundState,
    ongoingGames: i.ongoingGames,
  });

  const build = (
    phase: TournamentPhase,
    headline: string,
    detail: string | null = null,
    countdownTo: number | null = null,
    countdownLabel: string | null = null,
  ): TournamentStatus =>
    buildStatus(phase, headline, detail, countdownTo, countdownLabel, round);

  // 1 — ended beats everything.
  if (
    i.ended ||
    i.roundState === 'tournament_finished' ||
    i.raw?.is_active === false
  ) {
    return build(
      'ended',
      'Tournament finished',
      i.statusMessage || 'All rounds completed',
    );
  }

  // 2 — withdrawn.
  if (i.withdrawn) {
    return build(
      'withdrawn',
      'You withdrew from this tournament',
      'You can still watch the games',
    );
  }

  // 3/4 — not joined.
  if (!i.joined) {
    // the same `roundStarted` as the joined rows, plus the REST-only signal
    // a freshly loaded page has before any broadcast reaches it
    const started = roundStarted || !!i.raw?.round1_paired_at;
    if (started) {
      return build(
        'notJoined',
        `Round ${R} is under way`,
        isFinalRound
          ? "This is the final round, so joining now won't get you a game"
          : `Join now and you'll be paired from round ${R + 1}`,
      );
    }
    return build(
      'notJoined',
      "You haven't joined yet",
      'Join from the button below',
      startInFuture ? startDateMs : null,
      startInFuture ? 'Tournament starts in' : null,
    );
  }

  // 5 — a bye THIS round beats a live round (it IS this round's event).
  // Fix AA (2026-10-06, owner's words testing it): "make that text 'you got
  // a bye this round'" — a bye is a TOURNAMENT event, never the fabricated
  // game result the old lane showed it through (ARCHITECTURE §19).
  if (i.bye?.round === R) {
    return build(
      'bye',
      'You got a bye this round',
      `Round ${R}${liveGamesSuffix(i.ongoingGames)} · a free point${
        isFinalRound
          ? ' · that was the final round'
          : ` · you'll be paired again in round ${R + 1}`
      }`,
    );
  }

  // 6 — pairings generating.
  if (i.roundState === 'generating_pairings') {
    return build(
      'pairing',
      `Generating round ${R} pairings`,
      "Your board opens as soon as they're ready",
    );
  }

  // 7 — pairings published, my board not up yet.
  if (
    i.roundState === 'pairings_generated' &&
    !i.myGameLive &&
    i.roundStartAt !== null &&
    i.roundStartAt > i.nowMs
  ) {
    return build(
      'paired',
      `Round ${R} pairings are ready`,
      'Your game is about to start',
      i.roundStartAt,
      `Round ${R} starts in`,
    );
  }

  // 8 — my game is live.
  if (i.myGameLive) {
    return build(
      'playing',
      `Round ${R}${total > 0 ? ` of ${total}` : ''} in progress`,
      i.opponentName ? `You're playing ${i.opponentName}` : 'Your game is live',
    );
  }

  // 9 — the round is over, the next one is coming. S-x1: `nextRound` is
  // dormant until cc-client-sdk >= 2.2.1 is installed, so this phase renders
  // WITHOUT a countdown off `roundState === 'completed'` alone until then.
  if (i.roundState === 'completed' || i.nextRound) {
    return build(
      'break',
      `Round ${R} complete`,
      // a manual-start tournament HOLDS between rounds (manual round control,
      // 2026-08-31): nothing starts "soon" there, a human starts it
      i.nextRound
        ? null
        : i.raw?.round_start_mode === 'manual'
        ? 'The arbiter starts the next round'
        : 'Next round starting soon',
      i.nextRound?.startAt ?? null,
      // `next_round_timer_response` can omit `round` — R + 1 is the only
      // sane fallback (the round after the one that just completed).
      i.nextRound ? `Round ${i.nextRound.round ?? R + 1} starts in` : null,
    );
  }

  // 10 — the round is live and I have no board. `roundStarted`, not
  // `pairingGenerated` alone — see that constant's own comment (S10.1).
  if (roundStarted && (i.roundStartAt === null || i.roundStartAt <= i.nowMs)) {
    const late = isLateEntrant({raw: i.raw, bye: i.bye, round: R});
    return build(
      'sittingOut',
      `Round ${R} is under way${liveGamesSuffix(i.ongoingGames)}`,
      late
        ? `You joined after the pairings, so you'll be paired from round ${
            R + 1
          }`
        : "You're not paired this round",
    );
  }

  // 11 — joined, not started, a human starts the tournament. Once a round
  // exists this is row 10's job (a manual hold BETWEEN rounds is row 9's).
  if (!roundStarted && i.raw?.round_start_mode === 'manual') {
    return build(
      'awaitingArbiter',
      'Waiting for the arbiter to start the tournament',
      "You're in. The round begins when the arbiter starts it",
    );
  }

  // 12 — joined, not started, start_date in the future. `!roundStarted`: a
  // skewed clock must never put "starts in" over a round that is running.
  if (!roundStarted && startInFuture) {
    const playersCount = i.raw?.players_count ?? 0;
    return build(
      'registered',
      "You're in",
      playersCount > 0 ? `${playersCount} players joined` : null,
      startDateMs,
      'Tournament starts in',
    );
  }

  // 13 — joined, start time passed, nothing paired yet. `!roundStarted` is
  // what keeps "Starting soon…" out of a running round (S10.1).
  if (!roundStarted && hasStartDate) {
    return build(
      'registered',
      'Starting soon…',
      'Waiting for round 1 pairings',
    );
  }

  // 14 — anything else (defensive): no start_date to go on at all (e.g. the
  // board card's `raw: null`) and nothing round-related has happened yet.
  return build(
    'registered',
    i.statusMessage || `Waiting for round ${R} pairings`,
  );
}

/**
 * Fix T / T1 — the arena branch table (§T1 of
 * `FIX_T_ARENA_CLARITY_SPEC.md`), first match wins, copy EXACT. `round` is
 * always `null` here (an arena has no Swiss "round" to report).
 */
function arenaStatusFrom(i: TournamentStatusInput): TournamentStatus {
  const A = i.arena;
  const build = (
    phase: TournamentPhase,
    headline: string,
    detail: string | null = null,
    countdownTo: number | null = null,
    countdownLabel: string | null = null,
  ): TournamentStatus =>
    buildStatus(phase, headline, detail, countdownTo, countdownLabel, null);

  const finished = i.ended || A?.state === 'finished' || A?.state === 'void';
  /** 0 when `A` is null — never lets the `finishing` check below touch `A`. */
  const endsAtMs = A?.endsAtMs ?? 0;
  const finishing =
    A?.state === 'finishing' ||
    A?.state === 'finalizing' ||
    (!finished && endsAtMs > 0 && endsAtMs <= i.nowMs);
  /**
   * Resilience (independent of T4): the server's post-end fallback is
   * literally `{state: 'unknown', ..., endsAtMs: 0}` (facts §1.3) — treated
   * as "no state yet" exactly like the empty-string case ArenaLobby's own
   * rule already covers, so a stale/archived status read here can never be
   * mistaken for "paired when it starts". `finished` is still checked FIRST
   * (above), so this can never override a true `i.ended`.
   */
  const stateUnset = !A?.state || A?.state === 'unknown';
  const notStarted =
    A === null || A.state === 'created' || (stateUnset && !endsAtMs);
  const inGame = i.myGameLive || A?.myState === 'paired';

  const startDateMs = i.raw?.start_date ? Date.parse(i.raw.start_date) : NaN;
  const startInFuture = Number.isFinite(startDateMs) && startDateMs > i.nowMs;
  /** "Ends in", label `Arena ends in` — only while `endsAtMs > nowMs`. */
  const endsIn = (): [number | null, string | null] =>
    endsAtMs > i.nowMs ? [endsAtMs, 'Arena ends in'] : [null, null];

  // A1 — finished beats everything.
  if (finished) {
    const rank = A?.rank ?? 0;
    const points = A?.points ?? 0;
    return build(
      'ended',
      'Arena finished',
      rank > 0
        ? `You placed #${rank} · ${points} point${points === 1 ? '' : 's'}`
        : 'Final standings are on the Leaderboard tab',
    );
  }

  // A2 — withdrawn.
  if (i.withdrawn) {
    return build(
      'withdrawn',
      'You withdrew from this arena',
      'You can still watch the games',
    );
  }

  // A3 — the buzzer has gone; no countdown (by definition `endsAtMs` is
  // already past, or the server said so explicitly).
  if (finishing) {
    return build(
      'ending',
      'Arena is ending',
      inGame
        ? 'Finish your game — no new games will start'
        : 'No new games will start — waiting for the last games',
    );
  }

  // A4 — a spectator / not joined.
  if (!i.joined) {
    const [countdownTo, countdownLabel] = notStarted
      ? startInFuture
        ? [startDateMs, 'Arena starts in']
        : [null, null]
      : endsIn();
    return build(
      'notJoined',
      notStarted ? "Arena hasn't started" : 'Arena in progress',
      notStarted
        ? 'Join to be paired when it starts'
        : 'Join to be paired for a game',
      countdownTo,
      countdownLabel,
    );
  }

  // A5 — joined, the arena has not started.
  if (notStarted) {
    return build(
      'registered',
      "You're in",
      "You'll be paired automatically when the arena starts",
      startInFuture ? startDateMs : null,
      startInFuture ? 'Arena starts in' : null,
    );
  }

  // A6 — my game is live.
  if (inGame) {
    const [countdownTo, countdownLabel] = endsIn();
    return build(
      'playing',
      'Game in progress',
      A?.paused
        ? "Paused after this game — you won't be paired again until you resume"
        : i.opponentName
        ? `You're playing ${i.opponentName}`
        : 'Your game is live',
      countdownTo,
      countdownLabel,
    );
  }

  // A7 — paused, between games.
  if (A?.paused || A?.myState === 'paused') {
    const [countdownTo, countdownLabel] = endsIn();
    return build(
      'paused',
      "You're paused",
      'Resume to get your next game',
      countdownTo,
      countdownLabel,
    );
  }

  // A8 — pooled, waiting for the next pairing.
  if (A?.myState === 'pooled') {
    const [countdownTo, countdownLabel] = endsIn();
    return build(
      'pooled',
      'Finding your next game…',
      (A?.poolSize ?? 0) > 1
        ? `${A?.poolSize} players in the pool`
        : "You'll be paired as soon as an opponent is free",
      countdownTo,
      countdownLabel,
    );
  }

  // A9 — anything else: joined, the arena is running, I am simply not in
  // the pool.
  const [countdownTo, countdownLabel] = endsIn();
  return build(
    'sittingOut',
    "You're not in the pairing pool",
    'Join the pool to get a game',
    countdownTo,
    countdownLabel,
  );
}

export default tournamentStatusFrom;
